<template>
  <v-dialog v-model="dialogValue" max-width="500px" lazy>
    <v-card>
      <alert-banner :msg="alertMessage" />
      <v-form @submit.prevent="saveForm">
        <v-card-title>
          <span class="headline">
            Editing Item
          </span>
          <v-spacer />
          <v-btn icon @click="deleteFile">
            <v-icon color="grey lighten-1" small :loading="isDeleting">
              far fa-trash-alt
            </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap row justify-center>
              <v-flex xs11>
                <v-text-field v-model="form.name" v-validate="'required|noSpecialCharacters'" name="name" label="Name*" required :error-messages="errors.collect('name')" />
              </v-flex>
              <v-flex xs11>
                <v-textarea v-model="form.description" name="description" label="Description" :error-messages="errors.collect('description')" />
              </v-flex>
              <v-flex xs11>
                <v-text-field v-model="form.completionLength" name="completionLength" label="Completion Length" :error-messages="errors.collect('completionLength')" />
              </v-flex>
              <v-flex xs11>
                <v-btn v-if="!updatedFile" class="mx-0" @click="$refs.curriculumFileInput.click()">
                  Update Curriculum File
                </v-btn>
                <input id="curriculumUpload" ref="curriculumFileInput" type="file" name="file" :error-messages="errors.collect('file')" @change="updatedFile = $event.target.files[0]">
                <v-text-field v-if="updatedFile" v-model="updatedFile.name" append-icon="remove_circle_outline" readonly label="File Ready for Upload" @click:append="fileReset()" />
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <span class="pl-4">
            <small>*indicates required field</small>
          </span>
          <v-spacer />
          <v-btn flat @click="dialogValue = false">
            Cancel
          </v-btn>
          <v-btn type="submit" color="secondary darken-1" flat :loading="saving">
            Save changes
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
  import axios from 'axios';

  export default {
    props: {
      dialog: {
        type: Boolean,
        default: false,
      },

      courseId: {
        type: Number,
        default: null,
      },

      item: {
        type: Object,
        default: () => {},
      },
      items: {
        type: Array,
        default: () => [],
      },
      uncategorizedItems: {
        type: Array,
        default: () => [],
      },
    },

    data: () => ({
      defaultForm: {
        name: '',
        description: '',
        completionLength: '',
      },

      form: {
        name: '',
        description: '',
        completionLength: '',
      },

      updatedFile: '',
      directories: [],
      saving: false,
      isDeleting: false,
    }),

    computed: {
      dialogValue: {
        get() {
          return this.dialog;
        },
        set(value) {
          this.$emit('update:dialog', value);
        },
      },
    },

    watch: {
      dialogValue(val) {
        if (!val) {
          setTimeout(() => {
            this.clearErrors();
          }, 300);
        } else {
          this.form = JSON.parse(JSON.stringify(this.item));
          this.$validator.pause();
        }
      },
    },

    mounted() {
      this.getCategorizedDirectories();
    },

    methods: {
      async getCategorizedDirectories() {
        const { data } = await axios.get(`/api/courses/${this.courseId}/items/categorized/directories`);
        this.directories = data;
      },

      async saveCategorizedDirectories() {
        this.items.forEach((item, parentIndex) => {
          item.categorized = true;
          item.parentItemId = null;
          item.sortIndex = parentIndex;
          if (item.childItems) {
            item.childItems.forEach((child, childIndex) => {
              child.categorized = true;
              child.parentItemId = item.id;
              child.sortIndex = childIndex;
              if (child.childItems) {
                child.childItems.forEach((subChild, subChildIndex) => {
                  subChild.categorized = true;
                  subChild.parentItemId = child.id;
                  subChild.sortIndex = subChildIndex;
                });
              }
            });
          }
        });
        await axios.put(`/api/courses/${this.courseId}/items`, this.items);
        this.$emit('updateUnsavedState', false);
      },

      async getCategorized() {
        const { data } = await axios.get(`/api/courses/${this.courseId}/items/categorized`);
        this.$emit('update:items', data);
      },
      async getUncategorized() {
        const { data } = await axios.get(`/api/courses/${this.courseId}/items/uncategorized`);
        this.$emit('update:uncategorizedItems', data);
      },

      async deleteFile() {
        const result = confirm('Are you sure you want to delete this item?'); // eslint-disable-line
        if (result) {
          this.isDeleting = true;
          await axios.delete(`/api/courses/${this.courseId}/items/${this.item.id}`);
          await this.getCategorized();
          await this.getUncategorized();

          this.dialogValue = false;
          this.isDeleting = false;
        }
      },

      async saveForm() {
        const result = await this.startFormProcessing();
        if (!result) return;
        this.saving = true;
        try {
          await this.saveCategorizedDirectories();
          Object.keys(this.form).filter(key => key in this.item).forEach((key) => {
            if (key !== 'name' && key !== 'description' && key !== 'completionLength') {
              this.form[key] = this.item[key];
            }
          });
          await axios.patch(`/api/courses/${this.courseId}/items/${this.item.id}`, this.form);
          await this.submitFileUpdate();
          await this.getCategorized();
          this.dialogValue = false;
        } catch (error) {
          this.processErrors(error, this.addForm);
        } finally {
          this.saving = false;
        }
      },

      async submitFileUpdate() {
        if (!this.updatedFile) return;
        const form = new FormData();
        form.append('file', this.updatedFile);
        await axios.put(`/api/courses/${this.courseId}/items/${this.item.id}/file`, form, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        this.fileReset();
      },

      fileReset() {
        this.updatedFile = null;
        const input = this.$refs.curriculumFileInput;
        input.type = 'text';
        input.type = 'file';
      },
    },
  };
  </script>
  <style scoped>
  #curriculumUpload {
    position: absolute;
    left: -99999px;
  }
</style>